import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { PLAN_BASELINES, LIMIT_LABELS } from '@/lib/licensePlans';

// ADM-36: read-only license/usage summary tab. Backend contract is GET
// /api/settings/license — see the shape documented alongside this component's
// Plane issue. Any `limits.*.max` can be null (unrestricted/unconfigured) and
// must never be rendered as "X / null" or crash the usage bar math.

const LIMIT_ORDER = ['branches', 'users', 'leads_this_year', 'enrolled_students', 'courses', 'batches'];

// Optional features that aren't part of every plan. Keyed by the same name
// the backend's license.has_feature()/license.features dict uses.
const FEATURE_LABELS = {
  communication_hub: 'Communication Hub (WhatsApp/SMS/Email messaging)',
};

function fmtNum(n) {
  return (n || 0).toLocaleString('en-IN');
}

function fmtDate(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function LimitRow({ id, label, used, max, baseline }) {
  const hasMax = max !== null && max !== undefined;
  const pct = hasMax && max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
  const atLimit = hasMax && used >= max;
  const near = hasMax && !atLimit && max > 0 && used / max >= 0.85;
  const bonus = hasMax && baseline && max > baseline ? max - baseline : 0;

  return (
    <div data-testid={`license-limit-row-${id}`} className="py-2.5">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">{label}</span>
        <span className={`text-[11px] font-mono ${atLimit ? 'text-red-500 font-bold' : near ? 'text-amber-500 font-bold' : 'text-foreground'}`}>
          {hasMax ? `${fmtNum(used)} / ${fmtNum(max)}` : `${fmtNum(used)} · Unlimited`}
        </span>
      </div>
      {bonus > 0 && (
        <p data-testid={`license-bonus-${id}`} className="text-[9px] font-semibold mb-1.5" style={{ color: '#10A882' }}>
          Standard plan includes {fmtNum(baseline)} — you&rsquo;ve been given +{fmtNum(bonus)} bonus for this year
        </p>
      )}
      {hasMax && (
        <Progress
          value={pct}
          className={`h-1.5 ${atLimit ? 'bg-red-500/20 [&>div]:bg-red-500' : near ? 'bg-amber-500/20 [&>div]:bg-amber-500' : 'bg-primary/20'}`}
        />
      )}
    </div>
  );
}

export default function LicenseTab() {
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLicense = async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/api/settings/license');
        setLicense(data);
      } catch (e) {
        console.error(e);
        toast.error('Failed to load license information');
      } finally {
        setLoading(false);
      }
    };
    fetchLicense();
  }, []);

  if (loading) return <div className="text-sm text-muted-foreground text-center py-12 font-mono">loading...</div>;

  if (!license) {
    return (
      <div data-testid="license-tab" className="text-sm text-muted-foreground text-center py-12 font-mono">
        License information unavailable
      </div>
    );
  }

  const isExpired = !!license.is_expired;
  const isActive = license.status === 'active' && !isExpired;
  const planLabel = license.plan_name
    ? `${license.plan_name.charAt(0).toUpperCase()}${license.plan_name.slice(1)} Plan`
    : 'License';
  const baselines = PLAN_BASELINES[license.plan_name] || {};
  const excludedFeatures = Object.entries(license.features || {}).filter(([, included]) => included === false);

  return (
    <div data-testid="license-tab">
      <Card className="border border-border rounded-xl max-w-3xl"><CardContent className="p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-sm font-bold text-foreground">{planLabel}</h3>
          <span
            data-testid="license-status-pill"
            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
              isExpired
                ? 'bg-red-500/15 text-red-500'
                : isActive
                ? 'bg-emerald-500/15 text-emerald-600'
                : 'bg-secondary text-muted-foreground'
            }`}
          >
            {isExpired ? 'Expired' : license.status}
          </span>
        </div>

        <div data-testid="license-status-banner" className="mt-3 mb-4">
          {isExpired ? (
            <div className="flex items-start gap-2 p-3 rounded-xl border border-red-500/40 bg-red-500/10">
              <span className="text-[12px] font-semibold text-red-500">
                License expired on {fmtDate(license.valid_until)} &mdash; contact your vendor to renew.
              </span>
            </div>
          ) : (
            <p className="text-[11px] text-muted-foreground">Valid until {fmtDate(license.valid_until)}</p>
          )}
        </div>

        <h4 className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground mt-5 mb-2.5">Usage</h4>
        <div className="divide-y divide-border">
          {LIMIT_ORDER.map((key) => {
            const entry = license.limits?.[key] || {};
            return (
              <LimitRow key={key} id={key} label={LIMIT_LABELS[key]} used={entry.used} max={entry.max ?? null} baseline={baselines[key]} />
            );
          })}
        </div>

        {excludedFeatures.length > 0 && (
          <div className="mt-5 pt-4 border-t border-border">
            <h4 className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground mb-2.5">Not Included In Your Plan</h4>
            <div className="space-y-1.5">
              {excludedFeatures.map(([key]) => (
                <div key={key} data-testid={`license-excluded-feature-${key}`} className="flex items-center gap-2 text-[11px] text-muted-foreground">
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 shrink-0" />
                  <span>{FEATURE_LABELS[key] || key}</span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">Contact your vendor to add these to your plan.</p>
          </div>
        )}

        <div className="mt-5 pt-4 border-t border-border">
          <Link data-testid="license-terms-link" to="/settings/terms" className="text-[11px] font-semibold" style={{ color: '#E04B2A' }}>
            View Terms &amp; Conditions
          </Link>
        </div>
      </CardContent></Card>
    </div>
  );
}
